/**
 * Attachment Manager
 *
 * Handles file selection, validation, preview generation, and content part creation
 * for the composer attachment feature. Supports both images and documents.
 */

import { createAttachmentPreview } from "./attachment-preview";
import type {
  AgentWidgetAttachmentAdapter,
  AgentWidgetAttachmentsConfig,
  ContentPart,
  AgentWidgetConfig,
  AttachmentPreviewRenderContext,
  AttachmentPreviewRenderHandle
} from "../types";
import {
  fileToContentPart,
  validateFile,
  isImageFile,
  ALL_SUPPORTED_MIME_TYPES
} from "./content";

/** Lifecycle state of one pending attachment (roadmap section 8). */
export type PendingAttachmentStatus =
  | "processing"
  | "uploading"
  | "ready"
  | "error";

/**
 * Pending attachment with preview.
 *
 * `contentPart` is null until the adapter resolves: the tile appears
 * immediately in `processing`/`uploading` and only a `ready` attachment
 * contributes to the outgoing message.
 */
export interface PendingAttachment {
  id: string;
  file: File;
  previewUrl: string | null; // null for non-image files
  contentPart: ContentPart | null;
  status: PendingAttachmentStatus;
  /** 0 to 1, only while `uploading` and only when the adapter reports it. */
  progress?: number;
  error?: string;
  lineCount?: number;
}

/**
 * Attachment manager configuration
 */
export interface AttachmentManagerConfig {
  allowedTypes?: string[];
  maxFileSize?: number;
  maxFiles?: number;
  onFileRejected?: (file: File, reason: "type" | "size" | "count") => void;
  onAttachmentsChange?: (attachments: PendingAttachment[]) => void;
  /** Host upload adapter; without one, files convert to base64 in the browser. */
  adapter?: AgentWidgetAttachmentAdapter;
  preview?: AgentWidgetAttachmentsConfig["preview"];
  instanceId?: string;
  widgetConfig?: AgentWidgetConfig;
  renderAttachmentPreview?: (context: AttachmentPreviewRenderContext) => HTMLElement | AttachmentPreviewRenderHandle | null;
}

/**
 * Default adapter: the historical in-browser base64 conversion, wrapped in the
 * public interface. Reports no progress, so its tiles show `processing`.
 */
const BASE64_ADAPTER: AgentWidgetAttachmentAdapter = {
  add: (file) => fileToContentPart(file) as Promise<ContentPart>
};

const clampProgress = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
};

/**
 * Default configuration values
 */
const DEFAULTS = {
  allowedTypes: ALL_SUPPORTED_MIME_TYPES,
  maxFileSize: 10 * 1024 * 1024, // 10MB
  maxFiles: 4
};

/**
 * Generate a unique ID for attachments
 */
function generateAttachmentId(): string {
  return `attach_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Creates and manages attachments for the composer
 */
export class AttachmentManager {
  #attachments: PendingAttachment[] = [];
  #config: Required<
    Pick<AttachmentManagerConfig, "allowedTypes" | "maxFileSize" | "maxFiles">
  > &
    Pick<
      AttachmentManagerConfig,
      "onFileRejected" | "onAttachmentsChange" | "adapter" | "preview" | "widgetConfig" | "instanceId" | "renderAttachmentPreview"
    >;
  #lineCountsRequested = new Set<string>();
  #renders = new Map<string, AttachmentPreviewRenderHandle>();
  #previewsContainer: HTMLElement | null = null;
  /** In-flight `adapter.add` per attachment id; aborted on remove/clear/destroy. */
  #pendingAdds = new Map<string, AbortController>();
  /** The adapter that produced each ready part owns its eventual removal. */
  #partAdapters = new WeakMap<PendingAttachment, AgentWidgetAttachmentAdapter>();
  #destroyed = false;
  #releaseOnDestroy = false;

  constructor(config: AttachmentManagerConfig = {}) {
    this.#config = {
      allowedTypes: config.allowedTypes ?? DEFAULTS.allowedTypes,
      maxFileSize: config.maxFileSize ?? DEFAULTS.maxFileSize,
      maxFiles: config.maxFiles ?? DEFAULTS.maxFiles,
      onFileRejected: config.onFileRejected,
      onAttachmentsChange: config.onAttachmentsChange,
      preview: config.preview,
      widgetConfig: config.widgetConfig,
      instanceId: config.instanceId,
      renderAttachmentPreview: config.renderAttachmentPreview,
      adapter: config.adapter
    };
  }

  /** True when the host supplied an adapter (tiles then show upload state). */
  #hasCustomAdapter(): boolean {
    return typeof this.#config.adapter?.add === "function";
  }

  #adapter(): AgentWidgetAttachmentAdapter {
    return this.#hasCustomAdapter()
      ? (this.#config.adapter as AgentWidgetAttachmentAdapter)
      : BASE64_ADAPTER;
  }

  /**
   * Set the previews container element
   */
  setPreviewsContainer(container: HTMLElement | null): void {
    if (this.#previewsContainer !== container) this.#disposePreviews();
    this.#previewsContainer = container;
  }

  /**
   * Re-point previews at a new container and repaint from state. A composer
   * rebuild swaps the footer, so pending attachments must follow it.
   */
  remountPreviews(container: HTMLElement | null): void {
    this.#disposePreviews();
    this.#previewsContainer = container;
    if (container) {
      container.innerHTML = "";
      for (const attachment of this.#attachments) {
        this.#renderPreview(attachment);
      }
    }
    this.#updatePreviewsVisibility();
  }

  /**
   * Update the configuration (e.g., when allowed types change)
   */
  updateConfig(config: Partial<AttachmentManagerConfig>): void {
    if ("allowedTypes" in config) {
      this.#config.allowedTypes = config.allowedTypes?.length ? config.allowedTypes : DEFAULTS.allowedTypes;
    }
    if ("maxFileSize" in config) {
      this.#config.maxFileSize = config.maxFileSize ?? DEFAULTS.maxFileSize;
    }
    if ("maxFiles" in config) {
      this.#config.maxFiles = config.maxFiles ?? DEFAULTS.maxFiles;
    }
    if ("onFileRejected" in config) {
      this.#config.onFileRejected = config.onFileRejected;
    }
    if (config.onAttachmentsChange !== undefined) {
      this.#config.onAttachmentsChange = config.onAttachmentsChange;
    }
    if ("adapter" in config) this.#config.adapter = config.adapter;
    if ("preview" in config) this.#config.preview = config.preview;
    if ("widgetConfig" in config) this.#config.widgetConfig = config.widgetConfig;
    if ("instanceId" in config) this.#config.instanceId = config.instanceId;
    if ("renderAttachmentPreview" in config && config.renderAttachmentPreview !== this.#config.renderAttachmentPreview) {
      this.#config.renderAttachmentPreview = config.renderAttachmentPreview;
      this.remountPreviews(this.#previewsContainer);
    } else {
      for (const attachment of this.#attachments) { this.#updatePreview(attachment); void this.#readLineCount(attachment); }
    }
  }

  /** Every attachment holds a content part; the send gate reads this. */
  isReady(): boolean {
    return this.#attachments.every((a) => a.status === "ready");
  }

  /**
   * Get current attachments
   */
  getAttachments(): PendingAttachment[] {
    return [...this.#attachments];
  }

  /**
   * Get content parts for all attachments. Attachments that are still
   * uploading or errored contribute nothing; the send gate blocks that case
   * before it can be reached.
   */
  getContentParts(): ContentPart[] {
    return this.#attachments
      .map((a) => a.contentPart)
      .filter((part): part is ContentPart => part !== null);
  }

  /**
   * Check if there are any attachments
   */
  hasAttachments(): boolean {
    return this.#attachments.length > 0;
  }

  /**
   * Get the number of attachments
   */
  count(): number {
    return this.#attachments.length;
  }

  /**
   * Handle file input change event
   */
  async handleFileSelect(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    await this.handleFiles(Array.from(files));
  }

  /**
   * Handle an array of files (e.g., clipboard image paste)
   */
  async handleFiles(files: readonly File[]): Promise<void> {
    if (!files.length || this.#destroyed) return;

    const accepted: PendingAttachment[] = [];
    for (const file of files) {
      // Check if we've hit the max files limit
      if (this.#attachments.length >= this.#config.maxFiles) {
        this.#config.onFileRejected?.(file, "count");
        continue;
      }

      // Validate the file
      const validation = validateFile(
        file,
        this.#config.allowedTypes,
        this.#config.maxFileSize
      );

      if (!validation.valid) {
        const reason = validation.error?.includes("type") ? "type" : "size";
        this.#config.onFileRejected?.(file, reason);
        continue;
      }

      // The tile appears immediately, before the adapter has produced anything.
      const attachment: PendingAttachment = {
        id: generateAttachmentId(),
        file,
        previewUrl: isImageFile(file) ? URL.createObjectURL(file) : null,
        contentPart: null,
        status: this.#hasCustomAdapter() ? "uploading" : "processing",
        progress: this.#hasCustomAdapter() ? 0 : undefined
      };
      this.#attachments.push(attachment);
      this.#renderPreview(attachment);
      accepted.push(attachment);
      void this.#readLineCount(attachment);
    }

    this.#updatePreviewsVisibility();
    this.#notify();

    await Promise.all(accepted.map((attachment) => this.#runAdd(attachment)));
  }

  /**
   * Run `adapter.add` for one attachment and reconcile the result. Late
   * completions from an aborted or removed attachment write no state and log
   * nothing: the user already moved on.
   */
  async #runAdd(attachment: PendingAttachment): Promise<void> {
    const controller = new AbortController();
    this.#pendingAdds.set(attachment.id, controller);
    const adapter = this.#adapter();

    const stale = (): boolean =>
      this.#destroyed ||
      controller.signal.aborted ||
      this.#pendingAdds.get(attachment.id) !== controller ||
      !this.#attachments.includes(attachment);

    try {
      const part = await adapter.add(attachment.file, {
        signal: controller.signal,
        onProgress: (progress) => {
          if (stale() || attachment.status !== "uploading") return;
          attachment.progress = clampProgress(progress);
          this.#updatePreview(attachment);
          this.#notify();
        }
      });
      if (stale()) {
        // An adapter may finish after ignoring the abort. An abandoned draft
        // still owns that uploaded part and must release it on disposal.
        if (this.#destroyed && this.#releaseOnDestroy) this.#releasePart(part, adapter);
        return;
      }
      this.#partAdapters.set(attachment, adapter);
      attachment.contentPart = part;
      attachment.status = "ready";
      attachment.progress = undefined;
      attachment.error = undefined;
    } catch (error) {
      if (stale()) return;
      attachment.status = "error";
      attachment.progress = undefined;
      attachment.error =
        error instanceof Error ? error.message : String(error ?? "Upload failed");
    } finally {
      if (this.#pendingAdds.get(attachment.id) === controller) {
        this.#pendingAdds.delete(attachment.id);
      }
    }
    this.#updatePreview(attachment);
    this.#notify();
  }

  /** Retry a failed attachment from its error tile. */
  retryAttachment(id: string): void {
    const attachment = this.#attachments.find((a) => a.id === id);
    if (!attachment || attachment.status !== "error" || this.#destroyed) return;
    attachment.status = this.#hasCustomAdapter() ? "uploading" : "processing";
    attachment.progress = this.#hasCustomAdapter() ? 0 : undefined;
    attachment.error = undefined;
    this.#updatePreview(attachment);
    this.#notify();
    void this.#runAdd(attachment);
  }

  /** Abort an in-flight add without touching the attachment's own state. */
  #abortAdd(id: string): void {
    const controller = this.#pendingAdds.get(id);
    if (!controller) return;
    this.#pendingAdds.delete(id);
    controller.abort();
  }

  /**
   * Remove an attachment by ID. Aborts an in-flight upload, and hands a
   * completed one back to the adapter so remote storage can be released.
   */
  removeAttachment(id: string): void {
    const index = this.#attachments.findIndex((a) => a.id === id);
    if (index === -1) return;

    const attachment = this.#attachments[index];
    this.#abortAdd(id);

    // Revoke the object URL to free memory (only for images)
    if (attachment.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl);
    }

    // Remove from array
    this.#attachments.splice(index, 1);

    this.#disposePreview(id);
    this.#lineCountsRequested.delete(id);

    // Remove from DOM
    const previewEl = this.#previewsContainer?.querySelector(
      `[data-attachment-id="${id}"]`
    );
    if (previewEl) {
      previewEl.remove();
    }

    if (attachment.status === "ready" && attachment.contentPart) {
      this.#releasePart(attachment.contentPart, this.#partAdapters.get(attachment));
    }

    this.#updatePreviewsVisibility();
    this.#notify();
  }

  async #releasePart(
    part: ContentPart,
    adapter: AgentWidgetAttachmentAdapter | undefined
  ): Promise<void> {
    try {
      await adapter?.remove?.(part, { signal: new AbortController().signal });
    } catch (error) {
      console.error("[AttachmentManager] adapter.remove failed:", error);
    }
  }

  /**
   * Clear all attachments and abort anything in flight. This is also the
   * post-send path, so it never calls `adapter.remove`: the parts it drops
   * have just been handed to the model.
   */
  clearAttachments(): void {
    this.#disposePreviews();
    this.#lineCountsRequested.clear();
    for (const attachment of this.#attachments) {
      this.#abortAdd(attachment.id);
      if (attachment.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    }

    this.#attachments = [];
    this.#pendingAdds.clear();

    // Clear the previews container
    if (this.#previewsContainer) {
      this.#previewsContainer.innerHTML = "";
    }

    this.#updatePreviewsVisibility();
    this.#notify();
  }

  /**
   * Abort every in-flight upload and stop accepting new work. A discarded
   * conversation draft also releases ready uploads; ordinary widget teardown
   * keeps the historical behavior of retaining completed parts.
   */
  destroy(opts?: { release?: boolean }): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.#disposePreviews();
    this.#releaseOnDestroy = opts?.release === true;
    for (const controller of this.#pendingAdds.values()) controller.abort();
    this.#pendingAdds.clear();
    for (const attachment of this.#attachments) {
      if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
      if (this.#releaseOnDestroy && attachment.status === "ready" && attachment.contentPart) {
        this.#releasePart(attachment.contentPart, this.#partAdapters.get(attachment));
      }
    }
    this.#attachments = [];
    this.#previewsContainer = null;
  }

  #notify(): void {
    if (this.#destroyed) return;
    this.#config.onAttachmentsChange?.(this.getAttachments());
  }

  #previewContext(attachment: PendingAttachment): AttachmentPreviewRenderContext {
    const context: AttachmentPreviewRenderContext = {
      instanceId: this.#config.instanceId ?? "",
      state: Object.freeze({ id: attachment.id, name: attachment.file.name,
        mimeType: attachment.file.type, size: attachment.file.size, status: attachment.status,
        progress: attachment.progress, error: attachment.error, lineCount: attachment.lineCount,
        kind: isImageFile(attachment.file) ? "image" : "file", previewUrl: attachment.previewUrl }),
      config: { ...this.#config.widgetConfig, attachments: { ...this.#config.widgetConfig?.attachments, preview: this.#config.preview } },
      actions: { remove: () => this.removeAttachment(attachment.id), retry: () => this.retryAttachment(attachment.id) },
      defaultRenderer: () => createAttachmentPreview(context),
    };
    return context;
  }

  #renderPreview(attachment: PendingAttachment): void {
    if (!this.#previewsContainer) return;
    const context = this.#previewContext(attachment);
    let rendered: HTMLElement | AttachmentPreviewRenderHandle | null = null;
    try { rendered = this.#config.renderAttachmentPreview?.(context) ?? null; }
    catch (error) { console.error("[AttachmentManager] preview renderer failed:", error); }
    rendered ??= context.defaultRenderer();
    const handle = rendered instanceof HTMLElement ? { element: rendered } : rendered;
    handle.element.dataset.attachmentId = attachment.id;
    this.#renders.set(attachment.id, handle);
    this.#previewsContainer.appendChild(handle.element);
  }

  #updatePreview(attachment: PendingAttachment): void {
    try { this.#renders.get(attachment.id)?.update?.(this.#previewContext(attachment)); }
    catch (error) { console.error("[AttachmentManager] preview update failed:", error); }
  }

  #disposePreview(id: string): void {
    const handle = this.#renders.get(id);
    if (!handle) return;
    this.#renders.delete(id);
    try { handle.destroy?.(); } catch (error) { console.error("[AttachmentManager] preview cleanup failed:", error); } finally { handle.element.remove(); }
  }

  #disposePreviews(): void {
    for (const id of this.#renders.keys()) this.#disposePreview(id);
  }

  /** Never inspect binary formats; bounded text decoding does not block uploads. */
  async #readLineCount(attachment: PendingAttachment): Promise<void> {
    const file = attachment.file;
    if (this.#lineCountsRequested.has(attachment.id)) return;
    if (this.#config.preview?.file?.metadata !== "line-count" || file.size > 1024 * 1024 ||
      !["text/plain", "text/markdown", "text/csv", "application/json"].includes(file.type)) return;
    this.#lineCountsRequested.add(attachment.id);
    try {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      });
      if (this.#destroyed || !this.#attachments.includes(attachment) || text.includes("\0")) return;
      attachment.lineCount = text === "" ? 0 : text.split(/\r\n|\r|\n/).length - (/\r?\n$|\r$/.test(text) ? 1 : 0);
      this.#updatePreview(attachment);
    } catch { /* Metadata is optional and never changes upload state. */ }
  }

  /**
   * Update the visibility of the previews container
   */
  #updatePreviewsVisibility(): void {
    if (!this.#previewsContainer) return;
    this.#previewsContainer.style.display =
      this.#attachments.length > 0 ? "flex" : "none";
  }

  /**
   * Create an AttachmentManager from widget config
   */
  static fromConfig(
    config?: AgentWidgetAttachmentsConfig,
    onAttachmentsChange?: (attachments: PendingAttachment[]) => void
  ): AttachmentManager {
    // The public `attachments.onChange` is fed from the composer store, not
    // from here: this internal callback carries `File` handles and content
    // parts that never reach host code.
    return new AttachmentManager({
      allowedTypes: config?.allowedTypes,
      maxFileSize: config?.maxFileSize,
      maxFiles: config?.maxFiles,
      onFileRejected: config?.onFileRejected,
      onAttachmentsChange,
      preview: config?.preview,
      adapter: config?.adapter
    });
  }
}
